import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Droplets,
  Cog,
  Sparkles,
  ShieldCheck,
  Gauge,
  TrendingUp,
  AlertTriangle,
  Wrench,
  Phone,
  Mail,
  CheckCircle2,
  XCircle,
  Activity,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const ACCENT = "#E1132C";

const PILLARS = [
  {
    icon: Droplets,
    title: "Teljes körű csere",
    text:
      "Az olajnak közel 100%-át frissre cseréljük — a nyomatékváltóval és a hűtőkörrel együtt. Nem marad fáradt olaj a rendszerben.",
  },
  {
    icon: Sparkles,
    title: "Intenzív tisztítás",
    text:
      "Speciális Motul Transmission Clean adalékkal feloldjuk és kiöblítjük a lerakódásokat, fémkopadékokat és iszapot.",
  },
  {
    icon: Gauge,
    title: "Precíziós vezérlés",
    text:
      "A gép adatbázis alapján, grammra pontosan adagolja az előírt mennyiségű olajat, és folyamatosan figyeli a hőmérsékletet.",
  },
  {
    icon: TrendingUp,
    title: "Megnövelt élettartam",
    text:
      "Tiszta rendszer = finomabb váltások, jobb hatásfok, alacsonyabb fogyasztás és lényegesen hosszabb váltó-élettartam.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Tisztítás és öblítés",
    text:
      "A régi olajhoz speciális Motul tisztító adalékot adunk, amely a váltó járatása közben feloldja a szennyeződéseket és iszapot.",
  },
  {
    n: "02",
    title: "Gépi olajcsere",
    text:
      "A MotulEVO ATM 501s berendezés a régi olaj dinamikus kinyomásával párhuzamosan tölti be a friss, gyári specifikációnak megfelelő Motul ATF olajat.",
  },
  {
    n: "03",
    title: "Szűrő- és tömítéscsere",
    text:
      "Típustól függően kicseréljük az olajszűrőt, az olajteknő tömítését és a leeresztőcsavart — ahol gyári előírás.",
  },
  {
    n: "04",
    title: "Szintbeállítás és teszt",
    text:
      "Szoftveres szintellenőrzés (hőmérséklet-függő), adaptáció reset, majd dokumentált próbaút a finomabb váltások igazolására.",
  },
];

const WHEN = [
  {
    icon: Activity,
    title: "60 000 – 100 000 km",
    text:
      "Ha autója elérte ezt a futásteljesítményt — még ha a gyártó \"élettartam\" olajat is ír.",
  },
  {
    icon: AlertTriangle,
    title: "Darabos váltások",
    text:
      "Ha a váltás darabossá vált, rángat, késik vagy hideg motornál nehezen kapcsol.",
  },
  {
    icon: ShieldCheck,
    title: "Megelőzés",
    text:
      "Ha el szeretné kerülni a több százezer forintos váltófelújítást — a megelőzés a legolcsóbb.",
  },
];

const COMPARISON = {
  hagyomanyos: [
    { good: false, text: "Csak 40–60% olajcsere" },
    { good: false, text: "Régi, szennyezett olaj marad a citromban" },
    { good: false, text: "Új olaj azonnal elszennyeződik" },
    { good: false, text: "Nincs rendszertisztítás" },
    { good: false, text: "Olajszint csak \"érzésre\"" },
  ],
  motulevo: [
    { good: true, text: "Közel 100% olajcsere — beleértve a nyomatékváltót" },
    { good: true, text: "Speciális tisztító adalék öblíti a rendszert" },
    { good: true, text: "Új olaj makulátlan környezetbe kerül" },
    { good: true, text: "Hűtőkör + szeleptömb is átöblítve" },
    { good: true, text: "Grammra pontos, hőmérséklet-függő szintbeállítás" },
  ],
};

const FAQ = [
  {
    q: "Tényleg szükséges a váltóolaj-csere, ha a gyártó \"élettartam\" olajat ír?",
    a: "Igen. Az \"élettartam olaj\" marketing kifejezés — a valóságban az ATF folyadék 60–80 ezer kilométer körül elöregszik, oxidálódik és súrlódó képessége romlik. A megelőző csere törtrésze a felújítás árának.",
  },
  {
    q: "Mit jelent a MotulEVO ATM 501s gép?",
    a: "Egy professzionális, automatizált váltóolaj-csere berendezés, amely a váltó saját keringési rendszerét használva dinamikusan cseréli ki az olajat. Hőmérsékletet, nyomást és átfolyási mennyiséget folyamatosan figyel.",
  },
  {
    q: "Milyen váltókhoz alkalmas?",
    a: "ZF 5HP / 6HP / 8HP / 9HP, Aisin (BMW, Toyota, Lexus, Volvo), DSG / DCT (DQ200, DQ250, DQ381, DQ500), CVT (Multitronic, Lineartronic) és klasszikus 4–6 fokozatú automatákhoz egyaránt — szinte minden modern automataváltóhoz.",
  },
  {
    q: "Mennyi idő egy komplett MotulEVO csere?",
    a: "Átlagosan 1,5–2,5 óra, váltótípustól függően. Az olajteknő-leszerelés és szűrőcsere (ahol gyári) 30–60 perccel hosszabbít.",
  },
  {
    q: "Mennyibe kerül a szolgáltatás?",
    a: "Az ár erősen függ a váltótípustól és a szükséges olaj mennyiségétől (8–12 liter). Pontos árajánlatért hívjon vagy küldjön üzenetet — váltókódját ismerve azonnal kalkulálunk.",
  },
];

export default function AutomatavaltoPage() {
  useDocumentMeta({
    title:
      "Automataváltó olajcsere Debrecen — MotulEVO ATM 501s | Autó-Szerelem",
    description:
      "Professzionális automataváltó olajcsere Debrecenben MotulEVO ATM 501s géppel. ZF 6HP/8HP, Aisin, DSG, CVT váltókhoz. Közel 100% olajcsere, rendszeröblítés, adaptáció reset, dokumentált próbaút. Időpontfoglalás: +36 30 863 7476.",
    path: "/automatavalto-olajcsere",
  });

  // JSON-LD: Service + FAQPage for rich Google snippets
  React.useEffect(() => {
    const id = "automatavalto-jsonld";
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Automataváltó olajcsere",
        name: "Automataváltó olajcsere MotulEVO géppel",
        provider: {
          "@type": "AutoRepair",
          name: "Autó-Szerelem — Séra Autószervíz",
          telephone: "+36-30-863-7476",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Vadvirág utca 15/D — 3. kapu",
            addressLocality: "Debrecen",
            postalCode: "4031",
            addressCountry: "HU",
          },
        },
        areaServed: { "@type": "City", name: "Debrecen" },
        url: "https://autoszerelem.hu/automatavalto-olajcsere",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#0A0A0A]"
      data-testid="automatavalto-page"
    >
      <Navbar />

      {/* HERO */}
      <section
        className="relative pt-24 sm:pt-28 isolate overflow-hidden"
        data-testid="valto-hero-section"
      >
        {/* Red oily gradient background */}
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse at 80% 20%, ${ACCENT}55 0%, rgba(10,10,10,0) 55%), radial-gradient(ellipse at 15% 70%, rgba(255,140,0,0.18) 0%, rgba(10,10,10,0) 50%), #0A0A0A`,
            }}
          />
          {/* Diagonal mechanical pattern */}
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(60deg, #fff 0 1px, transparent 1px 24px)",
            }}
          />
          <Cog
            className="absolute -right-12 -top-8 w-[36rem] h-[36rem] text-white/[0.04] hidden lg:block"
            strokeWidth={0.6}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-16 sm:pt-6 sm:pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white mb-6 transition-colors"
            data-testid="valto-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>

          <div className="flex items-center gap-4 mb-8">
            <div className="m-stripe h-1 w-24" />
            <span
              className="text-[0.72rem] font-bold uppercase tracking-[0.32em] text-zinc-300"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              MotulEVO részleg / Debrecen
            </span>
          </div>

          <div
            className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 border border-white/15 bg-white/5 backdrop-blur"
            style={{ fontFamily: "Barlow Condensed" }}
          >
            <span
              className="text-[0.65rem] font-black uppercase tracking-[0.28em] text-white"
            >
              MotulEVO ATM 501s
            </span>
            <span className="w-1 h-1 bg-white/30" />
            <span className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-400">
              ZF · Aisin · DSG · CVT
            </span>
          </div>

          <h1
            className="font-black uppercase leading-[0.92] tracking-[-0.01em] text-white max-w-5xl"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2rem, 6.5vw, 6rem)",
            }}
            data-testid="valto-headline"
          >
            Professzionális automataváltó
            <br />
            <span style={{ color: ACCENT }}>MotulEVO</span> gépi olajcsere.
          </h1>

          <p
            className="mt-8 max-w-2xl text-zinc-300/90 text-base sm:text-lg leading-relaxed"
            data-testid="valto-subheadline"
          >
            Nem hagyományos leeresztés — <span className="text-white font-semibold">teljes
            rendszeröblítés</span> dinamikus eljárással. Közel 100% olajcsere,
            adaptáció reset, dokumentált próbaút. ZF 6HP/8HP, Aisin, DSG és CVT
            váltókhoz egyaránt.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/?service=Automatav%C3%A1lt%C3%B3%20olajcsere#foglalas"
              className="btn-primary inline-flex items-center"
              data-testid="valto-cta-booking"
            >
              Időpontfoglalás <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href="tel:+36308637476"
              className="btn-outline inline-flex items-center gap-2"
              data-testid="valto-cta-phone"
            >
              <Phone className="w-4 h-4" /> +36 30 863 7476
            </a>
          </div>

          {/* Quick facts strip */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { k: "~100%", v: "Olajcsere" },
              { k: "1,5–2,5 óra", v: "Átlagos időtartam" },
              { k: "Motul ATF", v: "Eredeti olaj" },
              { k: "ISTA reset", v: "Adaptáció" },
            ].map((s, i) => (
              <div
                key={s.v}
                className="bg-[#0d0d0d] px-5 py-5"
                data-testid={`valto-fact-${i}`}
              >
                <div
                  className="text-xl sm:text-2xl font-black uppercase text-white tracking-tight"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {s.k}
                </div>
                <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY traditional is not enough — Comparison */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="valto-comparison-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Miért nem elég a hagyományos?</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Hagyományos olajcsere
                <br />
                <span style={{ color: ACCENT }}>vs.</span> MotulEVO.
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              A gyári „élettartam" olaj is elöregszik. A gravitációs leeresztés
              csak a tartály tartalmát üríti — a probléma többi részét nem.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {/* Hagyományos */}
            <div className="bg-[#0d0d0d] p-8 sm:p-10" data-testid="valto-compare-old">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 flex items-center justify-center bg-zinc-700/30 border border-zinc-600/40">
                  <XCircle className="w-5 h-5 text-zinc-400" strokeWidth={1.6} />
                </div>
                <h3
                  className="text-xl sm:text-2xl font-bold uppercase text-zinc-400"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Hagyományos leeresztés
                </h3>
              </div>
              <ul className="space-y-3">
                {COMPARISON.hagyomanyos.map((c) => (
                  <li
                    key={c.text}
                    className="flex items-start gap-3 text-sm text-zinc-500 leading-relaxed line-through decoration-zinc-700/50"
                  >
                    <XCircle
                      className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-0.5"
                      strokeWidth={1.8}
                    />
                    <span>{c.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* MotulEVO */}
            <div
              className="relative bg-[#0d0d0d] p-8 sm:p-10"
              data-testid="valto-compare-motulevo"
              style={{
                background: `linear-gradient(135deg, rgba(225,19,44,0.08) 0%, #0d0d0d 60%)`,
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-11 h-11 flex items-center justify-center border"
                  style={{
                    backgroundColor: `${ACCENT}15`,
                    borderColor: `${ACCENT}50`,
                  }}
                >
                  <CheckCircle2
                    className="w-5 h-5"
                    style={{ color: ACCENT }}
                    strokeWidth={1.8}
                  />
                </div>
                <h3
                  className="text-xl sm:text-2xl font-bold uppercase text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  MotulEVO eljárás
                </h3>
              </div>
              <ul className="space-y-3">
                {COMPARISON.motulevo.map((c) => (
                  <li
                    key={c.text}
                    className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed"
                  >
                    <CheckCircle2
                      className="w-4 h-4 flex-shrink-0 mt-0.5"
                      style={{ color: ACCENT }}
                      strokeWidth={2}
                    />
                    <span>{c.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS — Benefits */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="valto-pillars-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">A MotulEVO előnyei</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Nem csak olajat cserélünk —
                <br />
                <span style={{ color: ACCENT }}>rendszert tisztítunk.</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              4 dolog, ami egy MotulEVO eljárás után másképp működik az autójában.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-[#0d0d0d] hover:bg-[#121212] transition-colors p-7"
                  data-testid={`valto-pillar-${p.title}`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center mb-6 border"
                    style={{
                      backgroundColor: `${ACCENT}10`,
                      borderColor: `${ACCENT}40`,
                    }}
                  >
                    <Icon
                      className="w-6 h-6"
                      style={{ color: ACCENT }}
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3
                    className="text-xl font-bold uppercase text-white leading-tight mb-3"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {p.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="valto-process-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">A folyamat</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                4 lépés —
                <br />
                <span style={{ color: ACCENT }}>tiszta eredmény.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {PROCESS.map((p) => (
              <div
                key={p.n}
                className="bg-[#0d0d0d] p-7 relative"
                data-testid={`valto-process-${p.n}`}
              >
                <div
                  className="text-5xl font-black tracking-tight"
                  style={{ fontFamily: "Barlow Condensed", color: ACCENT }}
                >
                  {p.n}
                </div>
                <h3
                  className="mt-3 text-lg font-bold uppercase text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="valto-when-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Mikor javasoljuk?</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Az időzítés
                <br />
                <span style={{ color: ACCENT }}>mindent eldönt.</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              A megelőzés csak töredéke a felújítás árának. Ne várja meg, hogy
              a probléma kialakuljon.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {WHEN.map((w) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.title}
                  className="service-card flex flex-col"
                  data-testid={`valto-when-${w.title}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-12 h-12 flex items-center justify-center border"
                      style={{
                        backgroundColor: `${ACCENT}10`,
                        borderColor: `${ACCENT}40`,
                      }}
                    >
                      <Icon
                        className="w-6 h-6"
                        style={{ color: ACCENT }}
                        strokeWidth={1.5}
                      />
                    </div>
                  </div>
                  <h3
                    className="text-xl font-bold uppercase text-white leading-tight mb-3"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {w.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {w.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING NOTE */}
      <section
        className="relative py-20 border-t border-white/5"
        data-testid="valto-pricing-note"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Wrench
            className="w-10 h-10 mx-auto mb-6"
            style={{ color: ACCENT }}
            strokeWidth={1.4}
          />
          <h3
            className="font-black uppercase text-white leading-[0.95]"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
            }}
          >
            Egyedi árazás váltótípus szerint.
          </h3>
          <p className="mt-5 text-zinc-400 leading-relaxed max-w-2xl mx-auto">
            A pontos ár erősen függ a váltótípustól (ZF 6HP/8HP, Aisin, DSG/DCT,
            CVT) és a szükséges olaj mennyiségétől (általában 8–12 liter).
            Hívjon vagy küldjön üzenetet — a váltókódját ismerve azonnal
            kalkulálunk Önnek pontos árajánlatot.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/?service=Automatav%C3%A1lt%C3%B3%20olajcsere#foglalas"
              className="btn-primary inline-flex items-center"
              data-testid="valto-pricing-cta-booking"
            >
              Ajánlatkérés <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href="tel:+36308637476"
              className="btn-outline inline-flex items-center gap-2"
              data-testid="valto-pricing-cta-phone"
            >
              <Phone className="w-4 h-4" /> Hívás most
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="valto-faq-section"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <span className="section-eyebrow mb-4">GYIK</span>
          <h2
            className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95] mb-12"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
            }}
          >
            Gyakori kérdések.
          </h2>

          <div className="space-y-px bg-white/5 border border-white/5">
            {FAQ.map((f) => (
              <details
                key={f.q}
                className="group bg-[#0d0d0d] hover:bg-[#121212] transition-colors"
                data-testid={`valto-faq-${f.q}`}
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span className="text-white font-semibold pr-4">{f.q}</span>
                  <span
                    className="w-7 h-7 flex items-center justify-center border border-white/20 text-lg leading-none transition-transform group-open:rotate-45 shrink-0"
                    style={{ color: ACCENT }}
                  >
                    +
                  </span>
                </summary>
                <div className="px-6 pb-6 text-sm text-zinc-400 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section
        className="relative py-20 border-t border-white/5"
        data-testid="valto-cta-banner"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative bg-gradient-to-br from-[#0d0d0d] to-[#0a0a0a] border border-white/10 p-10 sm:p-14 overflow-hidden">
            <div
              className="absolute -top-24 -right-24 w-72 h-72 blur-3xl pointer-events-none"
              style={{ backgroundColor: `${ACCENT}40` }}
            />
            <div className="m-stripe absolute top-0 left-0 right-0 h-1" />
            <Cog
              className="absolute -right-10 bottom-[-20px] w-64 h-64 text-white/[0.05] hidden md:block"
              strokeWidth={0.7}
            />

            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <span
                  className="text-[0.7rem] font-bold uppercase tracking-[0.24em]"
                  style={{ fontFamily: "Barlow Condensed", color: ACCENT }}
                >
                  Ne várjon a darabos váltásokig!
                </span>
                <h3
                  className="mt-3 font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                  }}
                >
                  Foglaljon időpontot
                  <br />
                  MotulEVO csere&shy;re még ma.
                </h3>
                <p className="mt-4 text-zinc-400 max-w-xl">
                  Egy óvatos megelőzés töredéke az árának egy váltó-felújításnak.
                  Adja meg autója adatait — 24 órán belül árajánlatot adunk.
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                <Link
                  to="/?service=Automatav%C3%A1lt%C3%B3%20olajcsere#foglalas"
                  className="btn-primary inline-flex items-center w-full md:w-auto justify-center"
                  data-testid="valto-cta-banner-booking"
                >
                  Időpontot foglalok <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <a
                  href="tel:+36308637476"
                  className="btn-outline inline-flex items-center gap-2 w-full md:w-auto justify-center"
                  data-testid="valto-cta-banner-phone"
                >
                  <Phone className="w-4 h-4" /> Hívás most
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4 text-center">
            <a
              href="tel:+36308637476"
              className="bg-[#0e0e0e] border border-white/5 hover:border-white/20 transition-colors p-5"
              data-testid="valto-contact-phone"
            >
              <Phone
                className="w-5 h-5 mx-auto mb-2"
                style={{ color: ACCENT }}
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Telefon
              </div>
              <div className="text-white font-medium mt-1">+36 30 863 7476</div>
            </a>
            <a
              href="mailto:autoszerelem@gmail.com"
              className="bg-[#0e0e0e] border border-white/5 hover:border-white/20 transition-colors p-5"
              data-testid="valto-contact-email"
            >
              <Mail
                className="w-5 h-5 mx-auto mb-2"
                style={{ color: ACCENT }}
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                E-mail
              </div>
              <div className="text-white font-medium mt-1">
                autoszerelem@gmail.com
              </div>
            </a>
            <div className="bg-[#0e0e0e] border border-white/5 p-5">
              <CheckCircle2
                className="w-5 h-5 mx-auto mb-2"
                style={{ color: ACCENT }}
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Cím
              </div>
              <div className="text-white font-medium mt-1">
                4031 Debrecen, Vadvirág u. 15/D
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
